import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { GoogleOAuthProvider } from '@react-oauth/google';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

const AuthContext = createContext(null);

// Dev-only escape hatch: `npm start` always builds with NODE_ENV=development,
// `npm run build`/`craco build` always with NODE_ENV=production, so this can
// never be true in a deployed build. Lets local dev skip Google entirely —
// no OAuth client, no backend Google-token verification needed.
const isDevAuth = process.env.NODE_ENV !== 'production';

const AuthProviderInner = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(res.data);
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.error('auth/me failed', e);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const loginWithCredential = async (credentialResponse) => {
    try {
      const res = await axios.post(
        `${API}/auth/session`,
        { id_token: credentialResponse.credential },
        { withCredentials: true }
      );
      setUser(res.data.user);
      return res.data.user;
    } catch (e) {
      console.error('Login failed', e);
      setUser(null);
      throw e;
    }
  };

  // Skips the Google round trip and the backend's token verification —
  // sets a plain mock user straight in state so local dev works without a
  // real OAuth client or a live backend/database.
  const devLogin = (email) => {
    const mockUser = {
      user_id: `dev_${email}`,
      email,
      name: email.split('@')[0],
      picture: null,
      plan: 'free',
    };
    setUser(mockUser);
    return mockUser;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.error('logout failed', e);
    }
    setUser(null);
  };

  const refresh = checkAuth;

  return (
    <AuthContext.Provider value={{ user, loading, loginWithCredential, devLogin, isDevAuth, logout, refresh, API }}>
      {children}
    </AuthContext.Provider>
  );
};

export const AuthProvider = ({ children }) => {
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  if (!googleClientId) {
    console.warn('REACT_APP_GOOGLE_CLIENT_ID not set');
  }

  return (
    <GoogleOAuthProvider clientId={googleClientId || ''}>
      <AuthProviderInner>{children}</AuthProviderInner>
    </GoogleOAuthProvider>
  );
};

export const useAuth = () => useContext(AuthContext);
